export type TickerPriceSource = {
  lastPrice?: string;
  midPrice?: string;
};

export type TickerDisplayPricePrefer = 'last' | 'mid';

function pickTickerDisplayPrice(source: TickerPriceSource, prefer: TickerDisplayPricePrefer): string | undefined {
  if (prefer === 'mid') {
    if (source.midPrice != null) return source.midPrice;
    if (source.lastPrice != null) return source.lastPrice;
    return undefined;
  }
  if (source.lastPrice != null) return source.lastPrice;
  if (source.midPrice != null) return source.midPrice;
  return undefined;
}

function getTickerDisplayPricePreferring(
  prefer: TickerDisplayPricePrefer,
  ...sources: (TickerPriceSource | null | undefined)[]
): string | undefined {
  for (const source of sources) {
    if (!source) continue;
    const price = pickTickerDisplayPrice(source, prefer);
    if (price != null) return price;
  }
  return undefined;
}

/**
 * Perpetual display / reference prices.
 *
 * ROLLBACK(last-price): MM 준비 완료 시 재활성화 — 원본: last 우선 (lastPrice → midPrice fallback).
 * MM 미준비로 mid 우선으로 임시 회귀. 아래 한 줄을 'last'로 되돌리면 perp 표시가 전체가 last 기준으로 복원된다.
 */
export function getTickerDisplayPrice(...sources: (TickerPriceSource | null | undefined)[]): string | undefined {
  // return getTickerDisplayPricePreferring('last', ...sources);
  return getTickerDisplayPricePreferring('mid', ...sources);
}

export function getTickerDisplayPriceOrDefault(...sources: (TickerPriceSource | null | undefined)[]): string {
  return getTickerDisplayPrice(...sources) ?? '0';
}

/**
 * Prefer midPrice; fall back to lastPrice.
 * Spot (esp. VARA) cannot rely on last trades — use BBO mid for display / reference.
 */
export function getSpotTickerDisplayPrice(...sources: (TickerPriceSource | null | undefined)[]): string | undefined {
  return getTickerDisplayPricePreferring('mid', ...sources);
}

export function getSpotTickerDisplayPriceOrDefault(...sources: (TickerPriceSource | null | undefined)[]): string {
  return getSpotTickerDisplayPrice(...sources) ?? '0';
}

/**
 * ROLLBACK(last-price): MM 준비 완료 시 재활성화 — 원본: spot은 mid, perp(`.PERP`)는 last 우선.
 * MM 미준비로 perp도 mid 우선으로 임시 회귀 (spot/perp 동일).
 */
export function getTickerDisplayPriceForSymbol(
  _symbol: string,
  ...sources: (TickerPriceSource | null | undefined)[]
): string | undefined {
  // const isSpot = !_symbol.endsWith('.PERP');
  // return getTickerDisplayPricePreferring(isSpot ? 'mid' : 'last', ...sources);
  return getTickerDisplayPricePreferring('mid', ...sources);
}

export function getTickerDisplayPriceForSymbolOrDefault(
  symbol: string,
  ...sources: (TickerPriceSource | null | undefined)[]
): string {
  return getTickerDisplayPriceForSymbol(symbol, ...sources) ?? '0';
}

/** Normalize position/market ticker fields (string or BigNumber) for display price reads. */
export function getPositionDisplayPrice(position: { midPrice?: unknown; lastPrice?: unknown }): string {
  return getTickerDisplayPriceOrDefault({
    lastPrice: position.lastPrice != null ? String(position.lastPrice) : undefined,
    midPrice: position.midPrice != null ? String(position.midPrice) : undefined,
  });
}
